import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faPlus,
  faChevronDown,
  faChevronUp,
  faTimes,
  faUpload,
  faCheck,
  faSearch,
  faDownload,
  faWandMagicSparkles
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { getEnabledPlatforms, isEnabledForAnyOfPlatforms } from '../utils/accountSettingPlatformList';
import { MMGY_PLAN_ONLY_IO_TYPE, MMGY_IO_TYPE_HELP, isMmgyPlanOnlyAvailable, effectiveMmgyIoType as resolveMmgyIoType } from '../utils/mmgyIoType';
import { useAccount } from '../contexts/AccountContext';
import { useEnabledChannels } from '../hooks/useEnabledChannels';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useChannelConfigs } from '../hooks/useChannelConfigs';
import { getUserData } from '../utils/userUtils';
import { canSendMessage, hasChannelAccess, isSuperAdmin } from '../utils/permissions';
import './CampaignCreation.css';

/**
 * CampaignCreation component for creating new campaigns
 * Implements the hierarchy: Account → Folder → Campaign
 * 
 * Features:
 * 1. Folder selection or creation
 * 2. Campaign name input
 * 3. Destination/channel selection based on account's enabled channels
 * 4. File upload for campaign assets
 * 5. Integration with existing chat-based campaign creation flow
 */

/**
 * Platforms that require an Advertiser ID at campaign creation, with the copy for each.
 *
 * One entry per platform: the requires-check, the field placeholder and the help text all
 * read from here. This used to be four separate places — the id list was duplicated at two
 * call sites and the copy lived in two parallel ternary chains — so adding a channel meant
 * four edits, and missing one silently produced either no required field at all or a field
 * labelled with the generic fallback. A channel is now added once.
 *
 * Keys are destination ids as they appear in AVAILABLE_DESTINATIONS below (and, for a
 * DB-backed channel, channel_types.name lowercased).
 */
export const ADVERTISER_ID_PLATFORMS = {
  'universal-ads': { placeholder: 'Enter Universal Ads Advertiser ID', help: 'The authorized Universal Ads ad account UUID.' },
  'dv360': {
    placeholder: 'Enter DV360 Advertiser ID',
    help: 'Your DV360 Advertiser ID (e.g., 12345678)',
  },
  'youtube-dv360': {
    placeholder: 'Enter YouTube DV360 Advertiser ID',
    help: 'Your YouTube DV360 Advertiser ID (e.g., 12345678)',
  },
  'ttd': {
    placeholder: 'Enter TTD Advertiser ID',
    help: 'Your Trade Desk Advertiser ID (e.g., abcd1234)',
  },
  'madhive': {
    placeholder: 'Enter MadHive Advertiser ID',
    help: 'Your MadHive Advertiser ID (e.g., adv_12345)',
  },
  'amazon-dsp': {
    placeholder: 'Enter Amazon DSP Advertiser ID',
    help: 'Your Amazon DSP Advertiser ID',
  },
  // Yahoo DSP calls it the advertiser (account) id and takes it per launch — it is
  // deliberately not part of the channel config, which is client_id + client_secret only.
  'yahoo': {
    placeholder: 'Enter Yahoo DSP Advertiser ID',
    help: 'Your Yahoo DSP advertiser (account) ID',
  },
  // In GAM the advertiser is a Company inside the network and varies per campaign,
  // so it is entered here rather than on the channel — the channel carries the
  // network-level service account, network code, trafficker and root ad unit.
  'gam': {
    // The placeholder is contract-shaped: advertiserIdPlatforms.test.js asserts
    // /^Enter .*Advertiser ID$/ across every entry, so the "(Company)" nuance —
    // GAM's name for the advertiser — goes in the help text, which is free-form.
    placeholder: 'Enter GAM Advertiser ID',
    help: 'The Google Ad Manager advertiser (Company) ID the order belongs to (e.g., 5736614534)',
  },
};

/**
 * The advertiser-id spec for a destination, or null if it does not require one.
 *
 * hasOwnProperty rather than `dest in` / a bare lookup: destination ids come from
 * channel_types.name, which an admin types, and every object inherits keys like
 * 'constructor' and 'toString'. `'constructor' in ADVERTISER_ID_PLATFORMS` is true and
 * ADVERTISER_ID_PLATFORMS['constructor'] is truthy, so either shortcut would make a
 * channel with such a name demand an Advertiser ID and then render an undefined
 * placeholder.
 */
export const getAdvertiserIdSpec = (destination) =>
  (destination && Object.prototype.hasOwnProperty.call(ADVERTISER_ID_PLATFORMS, destination))
    ? ADVERTISER_ID_PLATFORMS[destination]
    : null;

/**
 * Channels the AI Campaign mode can build for.
 *
 * `destination` is the ordinary create-form channel id, so selecting an AI
 * channel sets `selectedDestination` and every platform-specific field
 * (ad account / page / customer id, channel config picker) keeps working
 * exactly as it does in create mode. Keep in sync with the backend's
 * AI_CAMPAIGN_CHANNELS (services/aiCampaignBriefService.js).
 */
export const AI_CAMPAIGN_CHANNELS = [
  { id: 'fb', name: 'Facebook', destination: 'facebook', icon: 'facebook' },
  { id: 'sem', name: 'Google Search', destination: 'google-ads-search', icon: 'search' },
  { id: 'universal-ads', name: 'Universal Ads (Comcast)', destination: 'universal-ads', icon: 'live_tv' },
];

/** Campaign goals the AI launcher understands, with what each one changes. */
export const AI_CAMPAIGN_GOALS = [
  { id: 'reach', name: 'Reach', help: 'Maximize how many people see the ads.' },
  { id: 'performance', name: 'Performance', help: 'Drive clicks and traffic to the site.' },
];

const CampaignCreation = ({ embedded = false, onCancel } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccountIdToUse, accounts, currentAccountName } = useAccount();
  const [user] = useState(() => getUserData()); // Get user data once on mount
  const accountId = getAccountIdToUse();
  const { enabledChannels, isLoading: channelsLoading, error: channelsError, refetch: refetchEnabledChannels } = useEnabledChannels(accountId, user);
  const { settings: accountSettings, isMultiChannelEnabled, getMultiChannels, isLoading: settingsLoading } = useAccountSettings(accountId, user);
  const { getChannelsForPlatform, hasMultipleChannels, getDefaultChannelId, isLoading: channelConfigsLoading, refetch: refetchChannelConfigs } = useChannelConfigs(accountId, user);

  // Check for pre-filled data from media plan
  const prefillData = location.state?.prefill;

  // Component state
  const [mode, setMode] = useState('create'); // 'create', 'import' or 'ai'
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [selectedDestination, setSelectedDestination] = useState(''); // For single channel mode
  const [selectedDestinations, setSelectedDestinations] = useState([]); // For multi-channel mode
  const [useMultiChannelMode, setUseMultiChannelMode] = useState(false); // User's choice when multi-channel is available
  const [advertiserId, setAdvertiserId] = useState('');
  const [accountIdField, setAccountIdField] = useState(''); // For GroundTruth account ID
  const [profileId, setProfileId] = useState(''); // For Reddit profile ID
  const [pageId, setPageId] = useState(''); // For Facebook OAuth page ID
  const [adAccountId, setAdAccountId] = useState(''); // For LinkedIn ad account ID
  const [customerId, setCustomerId] = useState(''); // For Google Ads customer ID
  const [madhiveProductType, setMadhiveProductType] = useState('ctv'); // MadHive product type: CTV or Display
  const [ioType, setIoType] = useState('mmgy'); // IO type for MMGY accounts: mmgy, traveldesk or plan-only
  // YouTube DV360 launcher --inventory_mode CLI arg ('Standard' | 'Expanded').
  // Default reflects the backend handler's per-account fallback: 'Standard' for
  // MMGY, 'Expanded' otherwise. Only sent for youtube-dv360 destinations.
  const [inventoryMode, setInventoryMode] = useState('Expanded');
  const [selectedChannelId, setSelectedChannelId] = useState(null); // Selected channel config ID (for platforms with multiple configs)
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileData, setUploadedFileData] = useState([]); // Store GCS file data
  const [isLoading, setIsLoading] = useState(false);
  const [isReviewingCts, setIsReviewingCts] = useState(false);
  const [ctsReviewResult, setCtsReviewResult] = useState(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  // Re-validation of already-uploaded Excels after the MMGY IO type changed —
  // its own flag, so a cancelled run never leaves the upload's flag stuck on.
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState(null);

  // Planning mode state
  const [planningMode, setPlanningMode] = useState(false);

  // Import mode specific state
  const [platformCampaignId, setPlatformCampaignId] = useState(''); // Campaign ID to import

  // AI Campaign mode state — the brief the launcher builds the campaign from.
  // No files: these fields are the entire input (geo and context are optional).
  const [aiChannel, setAiChannel] = useState(''); // 'fb' | 'sem'
  const [aiGoal, setAiGoal] = useState('performance'); // 'reach' | 'performance'
  const [aiStartDate, setAiStartDate] = useState('');
  const [aiEndDate, setAiEndDate] = useState('');
  const [aiBudget, setAiBudget] = useState('');
  const [aiBrandUrl, setAiBrandUrl] = useState('');
  const [aiGeo, setAiGeo] = useState('');
  const [aiContext, setAiContext] = useState('');

  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [isAnalyzingFiles, setIsAnalyzingFiles] = useState(false);
  // Set when analyze-files rejects the upload because the 'io-required'
  // account flag is on and no insertion-order PDF was supplied. Such a
  // rejection nulls out fileAnalysis, so the requiresIo/hasIo pre-submit check
  // below can't fire — without this flag the error was shown but Create stayed
  // enabled and clicking it created the campaign anyway.
  //
  // Scoped to io-required on purpose: it is set ONLY for accounts that opted
  // into that flag, so an account without it behaves exactly as before. See
  // the analyze-files catch for why the launch-template rejection is excluded.
  const [accountRequirementError, setAccountRequirementError] = useState(null);

  // Folder search and pagination state
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [folderPage, setFolderPage] = useState(1);
  const [folderPagination, setFolderPagination] = useState(null);
  const [isLoadingMoreFolders, setIsLoadingMoreFolders] = useState(false);
  const folderDropdownRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Check if user has access to a channel (combines account channel enablement + user role permission)
  const userHasChannelAccess = (channelId) => {
    // Super Admin has access to all enabled channels
    if (isSuperAdmin(user)) return true;
    // Check user's role-based channel permission
    return hasChannelAccess(user, channelId);
  };

  // Available destinations based on enabled channels AND user's channel permissions
  const availableDestinations = [
    { id: 'dv360', name: 'DV360', icon: 'smart_display', enabled: enabledChannels?.dv360 },
    { id: 'ttd', name: 'The Trade Desk', icon: 'ads_click', enabled: enabledChannels?.ttd },
    { id: 'youtube-dv360', name: 'YouTube DV360', icon: 'play_circle', enabled: enabledChannels?.['youtube-dv360'] },
    { id: 'facebook', name: 'Facebook', icon: 'facebook', enabled: enabledChannels?.facebook },
    { id: 'google-ads-search', name: 'Google Search', icon: 'search', enabled: enabledChannels?.['google-ads-search'] },
    { id: 'google-ads-pmax', name: 'Google PMax', icon: 'ads_click', enabled: enabledChannels?.['google-ads-pmax'] },
    { id: 'google-ads-demandgen', name: 'Google DemandGen', icon: 'auto_awesome', enabled: enabledChannels?.['google-ads-demandgen'] },
    // TODO
    // { id: 'google_display', name: 'Google Display', icon: 'view_carousel', enabled: enabledChannels?.['google-ads-display'] },
    { id: 'tiktok', name: 'TikTok', icon: 'music_note', enabled: enabledChannels?.tiktok },
    { id: 'madhive', name: 'MadHive', icon: 'tv', enabled: enabledChannels?.madhive },
    { id: 'universal-ads', name: 'Universal Ads', icon: 'live_tv', enabled: enabledChannels?.['universal-ads'] },
    { id: 'amazon-dsp', name: 'Amazon DSP', icon: 'shopping_cart', enabled: enabledChannels?.['amazon-dsp'] },
    // Yahoo DSP. `enabled` comes from the enabled-channels API, which keys off
    // channel_types.name lowercased (routes/user.js:219), so assigning the yahoo channel
    // config to an account is enough to light this up — but ONLY once the row exists here:
    // a channel absent from this array can never render no matter what the DB says.
    { id: 'yahoo', name: 'Yahoo DSP', icon: 'campaign', enabled: enabledChannels?.yahoo },
    { id: 'gam', name: 'Google Ad Manager', icon: 'ad_units', enabled: enabledChannels?.['google-ad-manager'] },
    { id: 'groundtruth', name: 'GroundTruth', icon: 'location_on', enabled: enabledChannels?.groundtruth },
    { id: 'reddit', name: 'Reddit', icon: 'forum', enabled: enabledChannels?.reddit },
    { id: 'linkedin', name: 'LinkedIn', icon: 'work', enabled: enabledChannels?.linkedin },
    { id: 'openai', name: 'OpenAI', icon: 'smart_toy', enabled: enabledChannels?.openai }
  ].filter(dest => dest.enabled && userHasChannelAccess(dest.id));

  // Multi-channel support logic
  const isMultiChannelAvailable = isMultiChannelEnabled();
  const allowedChannels = getMultiChannels();
  // Planning mode is always single-select so it can capture a channel config
  // (persisted as campaigns.channel_id, like the normal new-campaign flow);
  // multi-channel select is never combined with planning mode.
  const isMultiChannelMode = isMultiChannelAvailable && useMultiChannelMode && !planningMode;

  // Destination filtering based on current mode
  const filteredDestinations = isMultiChannelMode
    ? availableDestinations.filter(dest =>
        dest.enabled && allowedChannels.includes(dest.id)
      )
    : availableDestinations.filter(dest => dest.enabled);

  // Get currently selected destinations (single or multiple)
  // Planning mode is single-select (like the normal new-campaign flow) so a
  // channel config can be picked and persisted as campaigns.channel_id; only
  // explicit multi-channel mode uses the selectedDestinations array.
  const getCurrentDestinations = () => {
    if (isMultiChannelMode) {
      return selectedDestinations;
    }
    return selectedDestination ? [selectedDestination] : [];
  };

  // Check if selected destination(s) require advertiser ID, account ID, profile ID, ad account ID, or customer ID
  const currentDestinations = getCurrentDestinations();

  // Planning mode: check if feature flag is enabled for any available platform.
  // Canonicalized so the setting works however it was typed in the admin UI —
  // 'g-ads-search' and 'google-ads-search' both resolve to the destination id
  // (an un-normalized 'g-ads-*' value matched nothing and silently disabled
  // planning mode for the account).
  const planningModeEnabledPlatforms = getEnabledPlatforms(accountSettings?.['planning-mode-enabled']);
  const isPlanningModeAvailable = planningModeEnabledPlatforms.length > 0 &&
    availableDestinations.some(d => planningModeEnabledPlatforms.includes(d.id));

  const requiresAdvertiserId = currentDestinations.some(dest => !!getAdvertiserIdSpec(dest));
  // Destination that drives the Advertiser ID field's placeholder/help text.
  // `selectedDestination` is set for both normal and planning mode (both are
  // single-select); only explicit multi-channel mode leaves it empty, so fall
  // back to the first selected platform that requires an advertiser ID.
  const advertiserIdDestination = selectedDestination
    || currentDestinations.find(dest => getAdvertiserIdSpec(dest))
    || '';
  const advertiserIdCopy = getAdvertiserIdSpec(advertiserIdDestination) || {
    placeholder: 'Enter Advertiser ID',
    help: 'Your platform-specific advertiser ID',
  };
  const isMadhive = currentDestinations.includes('madhive');
  // Prefer the AccountContext's `currentAccountName` (works for every role)
  // and fall back to the `accounts[]` list for legacy admin-only flows.
  // Regression context: `accounts[]` is only populated for regular admins,
  // so campaign-manager users were getting accountName='' here and the
  // Review CTS button was silently hidden.
  const accountName = currentAccountName
    || accounts?.find(a => String(a.id) === String(accountId))?.name
    || '';

  // Adara accounts use "Launch Template" wording for the review button/banner;
  // every other account keeps the historical "CTS" wording. Same matching
  // pattern as the MMGY check below.
  const isAdaraAccount = accountName &&
    (accountName.toLowerCase() === 'adara' || accountName.toLowerCase().startsWith('adara-'));
  const isGroundTruthAccount = accountName &&
    (accountName.toLowerCase() === 'groundtruth' || accountName.toLowerCase().startsWith('groundtruth-'));
  // Review CTS / Launch Template button visibility:
  //   - GroundTruth account: always show (any destination).
  //   - Adara account: only show when at least one destination is DV360 or TTD.
  //   - Any other account: never.
  const showReviewCtsButton =
    isGroundTruthAccount ||
    (isAdaraAccount &&
      (currentDestinations.includes('dv360') || currentDestinations.includes('ttd')));
  // Derive MMGY status from account settings (works for all user roles, including super admins
  // where the accounts list may not be populated)
  const isMMGYAccount = accountSettings?.['enable-mmgy-io-type'] !== undefined ||
    (accountName && (accountName.toLowerCase() === 'mmgy' || accountName.toLowerCase().startsWith('mmgy-')));
  // MMGY IO type the request carries: the picked one when the selector is shown
  // for a selected destination, else 'mmgy'; null for non-MMGY accounts.
  // 'plan-only' (Excel plan is the IO, no PDF) runs only on the MMGY DV360/TTD
  // pipeline, so it is offered only when every destination is one of those.
  const mmgyIoTypeEnabled = isMMGYAccount
    && isEnabledForAnyOfPlatforms(accountSettings?.['enable-mmgy-io-type'], currentDestinations);
  const mmgyPlanOnlyAvailable = isMmgyPlanOnlyAvailable(currentDestinations);
  const effectiveMmgyIoType = !isMMGYAccount
    ? null
    : (mmgyIoTypeEnabled ? resolveMmgyIoType(ioType, currentDestinations) : 'mmgy');
  const requiresAccountId = currentDestinations.some(dest => ['groundtruth'].includes(dest));
  const requiresProfileId = currentDestinations.some(dest => ['reddit'].includes(dest));
  const requiresCustomerId = currentDestinations.some(dest => ['google-ads-search', 'google-ads-pmax', 'google-ads-demandgen'].includes(dest));

  // Determine which platforms need ad account ID for dynamic placeholder/help text
  const hasLinkedIn = currentDestinations.includes('linkedin');
  const hasFacebook = currentDestinations.includes('facebook');

  // Check if selected Facebook channel has OAuth enabled
  const getSelectedChannelConfig = () => {
    if (!selectedChannelId || !selectedDestination) return null;
    const channels = getChannelsForPlatform(selectedDestination);
    return channels.find(ch => ch.id === selectedChannelId);
  };

  const selectedChannelConfig = getSelectedChannelConfig();
  const isFacebookOAuthChannel = hasFacebook && selectedChannelConfig?.field_values?.oauth === 'true';

  // Ad Account ID is required for LinkedIn, and for Facebook (both OAuth and non-OAuth)
  const requiresAdAccountId = currentDestinations.some(dest => {
    if (dest === 'linkedin') return true;
    if (dest === 'facebook') return true; // Always required for Facebook
    return false;
  });

  // Generate dynamic placeholder and help text for ad account ID
  const getAdAccountIdPlaceholder = () => {
    if (hasLinkedIn && hasFacebook) {
      return 'Enter Ad Account ID';
    } else if (hasLinkedIn) {
      return 'Enter LinkedIn Ad Account ID';
    } else if (hasFacebook) {
      return 'Enter Facebook Ad Account ID';
    }
    return 'Enter Ad Account ID';
  };

  // AI Campaign channels, gated three ways:
  //   1. the `ai-campaign` account setting names the channel — the feature flag,
  //      same platform-list convention as `import-campaign` above;
  //   2. the account has that channel enabled;
  //   3. this user has permission for it.
  //
  // A literal 'true' means every supported channel, matching the backend flag
  // (utils/aiCampaignFlag.js) — `import-campaign` does not accept 'true' and
  // silently disables itself when an admin types it, which is worth not
  // reproducing. The backend re-checks all of this: this gate only decides what
  // renders.
  const aiCampaignSetting = accountSettings?.['ai-campaign'];
  const aiFlagPlatforms = aiCampaignSetting
    && String(aiCampaignSetting).trim().toLowerCase() === 'true'
    ? AI_CAMPAIGN_CHANNELS.map(c => c.destination)
    : getEnabledPlatforms(aiCampaignSetting);
  const availableAiChannels = AI_CAMPAIGN_CHANNELS.filter(channel =>
    aiFlagPlatforms.includes(channel.destination)
    && enabledChannels?.[channel.destination]
    && userHasChannelAccess(channel.destination));

  // Selecting an AI channel selects the underlying destination, so the shared
  // platform-ID fields below light up without duplicating their logic.
  const handleAiChannelSelect = (channelId) => {
    const channel = AI_CAMPAIGN_CHANNELS.find(c => c.id === channelId);
    if (!channel) return;
    setAiChannel(channelId);
    if (channelId === 'universal-ads') setAiGoal('reach');
    setSelectedDestination(channel.destination);
    setSelectedDestinations([]);
    setUseMultiChannelMode(false);
    setError(null);
  };

  // The AI brief is complete: every required field filled, plus whatever
  // platform id the selected channel needs. Mirrors the checks handleAiSubmit
  // makes, so the button is never enabled for a request that would 400.
  const aiBriefComplete = !!(
    selectedFolder
    && campaignName.trim()
    && aiChannel
    && aiGoal
    && aiStartDate
    && aiEndDate
    && aiEndDate >= aiStartDate
    && Number(aiBudget) > 0
    && aiBrandUrl.trim()
    && (!requiresAdvertiserId || advertiserId.trim())
    && (!requiresAdAccountId || adAccountId.trim())
    && (!requiresCustomerId || customerId.trim())
    && (!isFacebookOAuthChannel || pageId.trim())
    && (!selectedDestination || !hasMultipleChannels(selectedDestination) || selectedChannelId)
  );

  const getAdAccountIdHelpText = () => {
    if (hasLinkedIn && hasFacebook) {
      return 'Your Ad Account ID (e.g., 516481389 for LinkedIn, 123456789 for Facebook)';
    } else if (hasLinkedIn) {
      return 'Your LinkedIn Ad Account ID (e.g., 516481389)';
    } else if (hasFacebook) {
      return 'Your Facebook Ad Account ID (e.g., 123456789)';
    }
    return 'Your Ad Account ID';
  };

  // Fetch folders on component mount
  useEffect(() => {
    if (accountId) {
      fetchFolders();
    }
  }, [accountId]);

  // Default inventory mode based on account: Standard for MMGY, Expanded
  // otherwise. Mirrors the campaign-services handler fallback so the UI
  // shows the same value the backend would pick if no override were sent.
  useEffect(() => {
    setInventoryMode(isMMGYAccount ? 'Standard' : 'Expanded');
  }, [isMMGYAccount]);

  // Plan-only is offered only for DV360/TTD — drop back to the default IO type
  // when the destination moves to one that cannot run it.
  useEffect(() => {
    if (ioType === MMGY_PLAN_ONLY_IO_TYPE && currentDestinations.length > 0 && !mmgyPlanOnlyAvailable) {
      setIoType('mmgy');
    }
  }, [ioType, mmgyPlanOnlyAvailable, currentDestinations.length]);

  // Handle pre-filled data from media plan
  useEffect(() => {
    if (prefillData) {
      // Pre-fill campaign name
      if (prefillData.campaignName) {
        setCampaignName(prefillData.campaignName);
      }

      // Pre-fill destination/platform
      if (prefillData.destination) {
        setSelectedDestination(prefillData.destination);
      }

      // Pre-fill uploaded files
      if (prefillData.uploadedFiles?.length > 0) {
        setUploadedFiles(prefillData.uploadedFiles);
      }

      // Pre-fill GCS file data
      if (prefillData.uploadedFileData?.length > 0) {
        setUploadedFileData(prefillData.uploadedFileData);
        setHasInteracted(true); // Mark as interacted since we have files
      }
    }
  }, [prefillData]);

  // Re-analyze files when destination (or the MMGY IO type, which decides
  // whether a PDF is needed) changes
  useEffect(() => {
    if (uploadedFileData.length > 0) {
      const destinations = getCurrentDestinations();
      if (destinations.length > 0) {
        analyzeFiles(uploadedFileData);
      }
    }
  }, [selectedDestination, selectedDestinations, ioType]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!showFolderDropdown) {
      setFolderSearchTerm('');
      setFolderPage(1);
      // Refetch folders without search when dropdown reopens
      if (accountId) {
        fetchFolders('', 1, false);
      }
    }
  }, [showFolderDropdown, accountId]);

  // Auto-select channel/destination if only one is available
  useEffect(() => {
    if (filteredDestinations.length === 1) {
      const singleDestination = filteredDestinations[0].id;

      if (isMultiChannelMode) {
        // In multi-channel mode, add to selectedDestinations if not already present
        if (!selectedDestinations.includes(singleDestination)) {
          setSelectedDestinations([singleDestination]);
        }
      } else {
        // In single-channel mode, set as selectedDestination if not already set
        if (!selectedDestination) {
          setSelectedDestination(singleDestination);
        }
      }
    }
  }, [filteredDestinations, isMultiChannelMode, selectedDestination, selectedDestinations]);

  // Auto-select channel config when destination changes (single channel mode only)
  useEffect(() => {
    if (selectedDestination && !isMultiChannelMode) {
      const channels = getChannelsForPlatform(selectedDestination);
      if (channels.length === 1) {
        // Auto-select if only one channel config
        setSelectedChannelId(channels[0].id);
      } else if (channels.length > 1) {
        // Reset selection if multiple configs exist (user must choose)
        setSelectedChannelId(null);
      } else {
        // No channel configs available
        setSelectedChannelId(null);
      }
    } else {
      // Multi-channel mode or no destination - reset channel selection
      setSelectedChannelId(null);
    }
  }, [selectedDestination, isMultiChannelMode, getChannelsForPlatform]);

  const fetchFolders = useCallback(async (searchTerm = '', page = 1, append = false) => {
    if (!accountId) return;

    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMoreFolders(true);
      }

      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          search: searchTerm,
          page,
          limit: 50, // Fetch 50 folders at a time
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      const newFolders = response.data.folders || response.data || [];

      if (append) {
        setFolders(prev => [...prev, ...newFolders]);
      } else {
        setFolders(newFolders);
      }

      setFolderPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching folders:', err);
      if (!append) {
        setError('Failed to load folders');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMoreFolders(false);
    }
  }, [accountId, user]);

  // Debounced search handler
  const handleFolderSearch = useCallback((searchTerm) => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout for debounced search
    searchDebounceRef.current = setTimeout(() => {
      setFolderPage(1);
      fetchFolders(searchTerm, 1, false);
    }, 300); // 300ms debounce
  }, [fetchFolders]);

  // Handle folder dropdown scroll for infinite loading
  const handleFolderDropdownScroll = useCallback((e) => {
    const element = e.target;
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (isNearBottom &&
        folderPagination?.hasNextPage &&
        !isLoadingMoreFolders &&
        !isLoading) {
      const nextPage = folderPage + 1;
      setFolderPage(nextPage);
      fetchFolders(folderSearchTerm, nextPage, true);
    }
  }, [folderPagination, isLoadingMoreFolders, isLoading, folderPage, folderSearchTerm, fetchFolders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !accountId) return;

    try {
      setIsLoading(true);
      const response = await api.post(getApiUrl('/api/v2/folders'), {
        name: newFolderName.trim(),
        accountId,
        userId: user?.userId || user?.id
      });

      const newFolder = response.data;
      setFolders(prev => [...prev, newFolder]);
      setSelectedFolder(newFolder);
      setNewFolderName('');
      setShowNewFolderForm(false);
      setShowFolderDropdown(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setHasInteracted(true);

    // Upload files to GCS immediately
    if (files.length > 0) {
      setIsUploadingFiles(true);
      try {
        const gcsFileData = await uploadFilesToGCS(files);

        // Replace files with the same name, or add new files
        let newUploadedFiles = [...uploadedFiles];
        let newUploadedFileData = [...uploadedFileData];

        files.forEach((file, index) => {
          const existingIndex = newUploadedFiles.findIndex(f => f.name === file.name);
          if (existingIndex !== -1) {
            // Replace existing file
            newUploadedFiles[existingIndex] = file;
            newUploadedFileData[existingIndex] = gcsFileData[index];
          } else {
            // Add new file
            newUploadedFiles.push(file);
            newUploadedFileData.push(gcsFileData[index]);
          }
        });

        setUploadedFiles(newUploadedFiles);
        setUploadedFileData(newUploadedFileData);

        // Analyze files after upload
        analyzeFiles(newUploadedFileData);
      } catch (error) {
        console.error('Error uploading files to GCS:', error);
        setError(`Failed to upload files: ${error.message}`);
      } finally {
        setIsUploadingFiles(false);
      }
    }

    // Reset the input value to allow re-uploading the same file
    event.target.value = '';
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedFileData(prev => prev.filter((_, i) => i !== index));
    // Re-analyze remaining files
    const remainingFiles = uploadedFileData.filter((_, i) => i !== index);
    if (remainingFiles.length > 0) {
      analyzeFiles(remainingFiles);
    } else {
      setFileAnalysis(null);
      // No files left to violate a requirement; the empty-upload check owns
      // the button from here.
      setAccountRequirementError(null);
    }
  };

  // Analyze uploaded files to determine orchestration type
  const analyzeFiles = async (fileData) => {
    if (!fileData || fileData.length === 0) {
      setFileAnalysis(null);
      setAccountRequirementError(null);
      return;
    }

    try {
      setIsAnalyzingFiles(true);
      // Clear any previous requirement rejection — this run decides afresh
      // (e.g. the user just added the missing IO PDF, or picked a platform the
      // flag doesn't cover, which re-triggers analysis).
      setAccountRequirementError(null);

      const analysisPayload = {
        files: fileData.map(file => ({
          gcsPath: file.gcsPath,
          originalName: file.originalName,
          size: file.size,
          type: file.type
        })),
        platform: selectedDestination,
        context: {
          campaignName: campaignName.trim(),
          advertiserId: advertiserId.trim()
        },
        // MMGY plan-only: the Excel plan is the IO, so no PDF is required
        ...(effectiveMmgyIoType ? { ioType: effectiveMmgyIoType } : {})
      };

      const response = await api.post(
        getApiUrl('/api/v2/orchestration/analyze-files'),
        {
          ...analysisPayload,
          accountId,
          userId: user?.userId || user?.id
        }
      );

      const analysis = response.data.analysis;
      setFileAnalysis(analysis);

      console.log('📊 File analysis result:', {
        recommendedType: analysis?.recommendedType,
        confidence: analysis?.confidence,
        reasoning: analysis?.reasoning,
        fileCount: fileData.length,
        isMultiPlatform: analysis?.multiPlatform?.isMultiPlatform
      });

      // Check if multi-platform JSON was detected
      if (analysis?.multiPlatform?.isMultiPlatform) {
        console.log('🌐 Multi-platform JSON detected:', analysis.multiPlatform);

        // If multi-channel mode is available and platforms match allowed channels, auto-enable it
        if (isMultiChannelAvailable) {
          const detectedPlatforms = analysis.multiPlatform.platforms || [];
          const matchingPlatforms = detectedPlatforms.filter(p => allowedChannels.includes(p));

          if (matchingPlatforms.length >= 2) {
            // Auto-enable multi-channel mode and pre-select detected platforms
            setUseMultiChannelMode(true);
            setSelectedDestinations(matchingPlatforms);
            console.log(`✅ Auto-enabled multi-channel mode with platforms: ${matchingPlatforms.join(', ')}`);
          }
        }
      }
    } catch (error) {
      console.error('Error analyzing files:', error);

      // Account-requirement rejections carry a user-actionable message — show
      // it. Anything else stays silent (analysis is advisory).
      // 'Insertion order required' is the io-required flag's error; without it
      // listed here the 400 would fall to the else below and the user would see
      // NOTHING, then be allowed to submit — the pre-submit check at the bottom
      // of handleCreateCampaign cannot fire because fileAnalysis is null.
      const requirementErrors = ['Launch template required', 'Insertion order required'];
      if (error.response?.status === 400 && requirementErrors.includes(error.response?.data?.error)) {
        setError(error.response.data.message);
        setFileAnalysis(null);
        // Disable Create for io-required ONLY — deliberately NOT for the
        // launch-template rejection, whose behavior must stay exactly as it
        // was. The two gates have different account exclusions: this route's
        // launch-template 400 excludes only MMGY, while the dispatcher's
        // equivalent also excludes Crimtan / IDFC / Adara. So one of those
        // accounts can get a 400 here on a request the dispatcher would then
        // ACCEPT — today the user clicks Create and the launch succeeds.
        // Disabling the button on that error would block a working flow.
        if (error.response.data.error === 'Insertion order required') {
          setAccountRequirementError(error.response.data.message);
        }
      } else {
        // For other errors, don't show error to user, just continue without analysis
        setFileAnalysis(null);
      }
    } finally {
      setIsAnalyzingFiles(false);
    }
  };

  // Helper function to detect platform from filename
  // Only validates if filename contains platform AND "launch" AND "template"
  const detectPlatformFromFilename = (filename) => {
    const lowerName = filename.toLowerCase();

    // Must contain both "launch" and "template"
    if (!lowerName.includes('launch') || !lowerName.includes('template')) {
      return null;
    }

    // Check for platform
    if (lowerName.includes('dv360') || lowerName.includes('dv_360')) {
      return 'dv360';
    }
    if (lowerName.includes('ttd') || lowerName.includes('tradedesk')) {
      return 'ttd';
    }

    return null;
  };

  // Merge per-platform MMGY IO validation responses into a single
  // { valid, errors, warnings, metadata } object. Tags messages with the
  // platform when more than one was validated, so the user can tell which
  // DSP each issue relates to.
  const mergeMmgyValidations = (responses) => {
    const successful = responses.filter(r => r && r.validation);
    if (successful.length === 0) return null;

    const errors = [];
    const warnings = [];
    const metadata = { platforms: {} };
    const tagMessages = successful.length > 1;

    for (const { platform, validation } of successful) {
      const tag = tagMessages ? `[${platform.toUpperCase()}] ` : '';
      (validation.errors || []).forEach(e => errors.push(`${tag}${e}`));
      (validation.warnings || []).forEach(w => warnings.push(`${tag}${w}`));
      metadata.platforms[platform] = validation.metadata || {};
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      metadata,
    };
  };

  // MMGY IO pre-flight validation of one uploaded Excel, per selected DSP,
  // under the given IO type. Returns the merged validation (or null).
  const runMmgyIoValidation = async (fileData, platforms, validationIoType) => {
    const responses = await Promise.all(
      platforms.map(p =>
        api.post(getApiUrl('/api/upload/validate-excel'), {
          gcsPath: fileData.gcsPath,
          platform: p,
          accountId: accountId,
          ioType: validationIoType
        }).then(r => ({ platform: p, validation: r.data?.validation }))
          .catch(err => {
            console.error(`MMGY IO validation failed for ${p} (non-blocking):`, err);
            return null;
          })
      )
    );
    return mergeMmgyValidations(responses);
  };

  // An Excel validated under one IO type is re-validated when the IO type
  // changes — plan-only runs extra checks (the IO is built from the Excel),
  // and a stale result from the other type must not keep blocking Create.
  useEffect(() => {
    if (!isMMGYAccount || accountSettings?.['validate-excel'] !== 'true') return undefined;
    const platforms = currentDestinations.filter(d => d === 'dv360' || d === 'ttd');
    // Also covers a file whose upload finished under the previous IO type.
    const stale = uploadedFileData.filter(f =>
      f?.mmgyValidationIoType && f.mmgyValidationIoType !== effectiveMmgyIoType);
    if (stale.length === 0 || platforms.length === 0) return undefined;
    let cancelled = false;
    setIsRevalidating(true);
    Promise.all(stale.map(async (f) => ({
      gcsPath: f.gcsPath,
      validation: await runMmgyIoValidation(f, platforms, effectiveMmgyIoType),
    }))).then((results) => {
      if (cancelled) return;
      setUploadedFileData(prev => prev.map(f => {
        const hit = results.find(r => r.gcsPath === f.gcsPath);
        if (!hit) return f;
        const next = { ...f, mmgyValidationIoType: effectiveMmgyIoType };
        if (hit.validation) next.validation = hit.validation; else delete next.validation;
        return next;
      }));
    }).finally(() => { if (!cancelled) setIsRevalidating(false); });
    return () => { cancelled = true; setIsRevalidating(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMmgyIoType, uploadedFileData]);

  // Upload files to GCS using existing /api/upload route
  const uploadFilesToGCS = async (files) => {
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', accountId); // Use accountId from context instead of user.accountId
      formData.append('userId', user?.userId || user?.id);

      const response = await api.post(getApiUrl('/api/upload'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const fileData = {
        originalName: file.name,
        gcsPath: response.data.data.gcsPath,
        publicUrl: response.data.data.publicUrl,
        expiresAt: response.data.data.expiresAt,
        size: file.size,
        type: file.type
      };

      // Check if validation is enabled and file is Excel
      const isExcel = file.name.match(/\.(xlsx|xls|xlsm)$/i);
      const launchPlatform = detectPlatformFromFilename(file.name);
      const mmgyApplicablePlatforms = isMMGYAccount
        ? currentDestinations.filter(d => d === 'dv360' || d === 'ttd')
        : [];
      const validationEnabled = accountSettings?.['validate-excel'] === 'true';

      if (isExcel && validationEnabled && launchPlatform) {
        // Existing path: launch-sheet template validation
        try {
          console.log('Running Excel validation for:', file.name);
          setIsValidating(true);

          const validationResponse = await api.post(
            getApiUrl('/api/upload/validate-excel'),
            {
              gcsPath: fileData.gcsPath,
              platform: launchPlatform,
              accountId: accountId
            }
          );

          if (validationResponse.data.validation) {
            fileData.validation = validationResponse.data.validation;

            const { valid, errors, warnings } = validationResponse.data.validation;
            if (!valid && errors.length > 0) {
              console.error('Validation errors:', errors);
            } else if (warnings.length > 0) {
              console.warn('Validation warnings:', warnings);
            }
          }

          setIsValidating(false);
        } catch (validationError) {
          console.error('Validation failed (non-blocking):', validationError);
          setIsValidating(false);
        }
      } else if (isExcel && validationEnabled && mmgyApplicablePlatforms.length > 0) {
        // MMGY IO pre-flight validation: run per selected DSP and merge results
        try {
          console.log(`Running MMGY IO validation for: ${file.name} (ioType=${effectiveMmgyIoType}, platforms=${mmgyApplicablePlatforms.join(',')})`);
          setIsValidating(true);

          const merged = await runMmgyIoValidation(fileData, mmgyApplicablePlatforms, effectiveMmgyIoType);
          fileData.mmgyValidationIoType = effectiveMmgyIoType;
          if (merged) {
            fileData.validation = merged;
            if (!merged.valid && merged.errors.length > 0) {
              console.error('MMGY IO validation errors:', merged.errors);
            } else if (merged.warnings.length > 0) {
              console.warn('MMGY IO validation warnings:', merged.warnings);
            }
          }

          setIsValidating(false);
        } catch (validationError) {
          console.error('MMGY IO validation failed (non-blocking):', validationError);
          setIsValidating(false);
        }
      }

      return fileData;
    });

    return Promise.all(uploadPromises);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    setHasInteracted(true);

    // Upload files to GCS immediately
    if (files.length > 0) {
      setIsUploadingFiles(true);
      try {
        const gcsFileData = await uploadFilesToGCS(files);

        // Replace files with the same name, or add new files
        let newUploadedFiles = [...uploadedFiles];
        let newUploadedFileData = [...uploadedFileData];

        files.forEach((file, index) => {
          const existingIndex = newUploadedFiles.findIndex(f => f.name === file.name);
          if (existingIndex !== -1) {
            // Replace existing file
            newUploadedFiles[existingIndex] = file;
            newUploadedFileData[existingIndex] = gcsFileData[index];
          } else {
            // Add new file
            newUploadedFiles.push(file);
            newUploadedFileData.push(gcsFileData[index]);
          }
        });

        setUploadedFiles(newUploadedFiles);
        setUploadedFileData(newUploadedFileData);

        // Analyze files after upload
        analyzeFiles(newUploadedFileData);
      } catch (error) {
        console.error('Error uploading files to GCS:', error);
        setError(`Failed to upload files: ${error.message}`);
      } finally {
        setIsUploadingFiles(false);
      }
    }
  };

  // Handle Review CTS — run semantic validation on uploaded GT Excel before campaign creation
  const handleReviewCts = async () => {
    if (isReviewingCts) return;

    // Find the Excel file in uploadedFileData
    const excelFile = uploadedFileData.find(f =>
      f.gcsPath && (f.gcsPath.endsWith('.xlsx') || f.gcsPath.endsWith('.xls') || f.gcsPath.endsWith('.xlsm') || f.gcsPath.endsWith('.zip'))
    );

    if (!excelFile) {
      alert('Please upload an Excel file first.');
      return;
    }

    // Pick platform: GT wins when present (preserves existing GT-only behavior).
    // Otherwise use the first DV360/TTD destination so the Adara validator runs.
    const destinations = getCurrentDestinations();
    let platform = '';
    if (destinations.includes('groundtruth')) {
      platform = 'groundtruth';
    } else if (destinations.includes('dv360')) {
      platform = 'dv360';
    } else if (destinations.includes('ttd')) {
      platform = 'ttd';
    }

    setIsReviewingCts(true);
    setCtsReviewResult(null);

    try {
      // Call the review-cts endpoint directly (using the upload validate pattern)
      // Since campaign doesn't exist yet, we use the file upload review endpoint
      const response = await api.post(
        getApiUrl('/api/upload/review-cts'),
        {
          gcsPath: excelFile.gcsPath,
          accountId: accountIdField || '',
          workspaceAccountId: accountId,
          userId: user?.userId || user?.id,
          platform,
        }
      );

      console.log('📋 CTS review result:', response.data);
      setCtsReviewResult(response.data.result);
    } catch (error) {
      console.error('CTS review failed:', error);
      setCtsReviewResult({
        success: false,
        errors: [`Review failed: ${error.response?.data?.error || error.message}`],
        warnings: [],
        infos: [],
      });
    } finally {
      setIsReviewingCts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate basic required fields
    if (!selectedFolder || !campaignName.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate destination selection - always require at least 1 platform
    const destinations = getCurrentDestinations();
    if (destinations.length === 0) {
      setError(isMultiChannelMode ? 'Please select at least one destination' : 'Please select a destination');
      return;
    }

    // Advertiser ID is required in both planning and non-planning mode — captured upfront
    // and persisted at creation so it no longer needs to be entered at activation time.
    if (requiresAdvertiserId && !advertiserId.trim()) {
      setError('Advertiser ID is required for this destination');
      return;
    }

    // Skip the remaining platform-specific field validations in planning mode
    if (!planningMode) {
      if (requiresAccountId && !accountIdField.trim()) {
        setError('Account ID is required for this destination');
        return;
      }

      if (requiresProfileId && !profileId.trim()) {
        setError('Profile ID is required for Reddit campaigns');
        return;
      }

      // Page ID is required for Facebook OAuth channels
      if (isFacebookOAuthChannel && !pageId.trim()) {
        setError('Page ID is required for Facebook OAuth channels');
        return;
      }

      if (requiresAdAccountId && !adAccountId.trim()) {
        setError('Ad Account ID is required for LinkedIn/Facebook campaigns');
        return;
      }

      if (requiresCustomerId && !customerId.trim()) {
        setError('Customer ID is required for Google Ads campaigns');
        return;
      }

      // Validate channel config selection when multiple configs exist (single
      // channel mode — which now includes planning mode — only).
      if (!isMultiChannelMode && selectedDestination && hasMultipleChannels(selectedDestination) && !selectedChannelId) {
        setError('Please select a channel configuration for this platform');
        return;
      }
    }

    if (uploadedFiles.length === 0) {
      setHasInteracted(true); // Show error state
      setError('Please upload at least one file to create the campaign');
      // Scroll to file upload area
      document.getElementById('file-upload')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }

    // An io-required rejection from analyze-files nulls fileAnalysis, so the
    // requiresIo check below cannot see it — stop here instead of creating the
    // campaign and failing later at dispatch. (The Create button is disabled in
    // this state; this guards programmatic submits and any state race.)
    if (accountRequirementError) {
      setHasInteracted(true);
      setError(accountRequirementError);
      document.getElementById('file-upload')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }

    // Check if launch template is required but missing
    if (fileAnalysis?.requiresTemplate && !fileAnalysis?.hasTemplate) {
      setHasInteracted(true);
      setError('This account requires a launch template file (JSON or Excel with "launch" or "template" in the filename). Please upload a launch template to proceed.');
      document.getElementById('file-upload')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }

    // Check if the insertion order PDF is required but missing ('io-required'
    // account flag). Sibling of the launch-template check above; both flags
    // ride the same analyze-files response, and requiresIo is false unless the
    // account enabled the flag for this platform.
    if (fileAnalysis?.requiresIo && !fileAnalysis?.hasIo) {
      setHasInteracted(true);
      setError('This account requires an insertion order (IO) PDF. Please upload the insertion order PDF to proceed.');
      document.getElementById('file-upload')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return;
    }

    if (!canSendMessage(user)) {
      setError('You do not have permission to create campaigns');
      return;
    }

    try {
      setIsLoading(true);
      
      // Create campaign record
      const destinations = getCurrentDestinations();
      const useMultiSelect = isMultiChannelMode;
      const campaignData = {
        name: campaignName.trim(),
        folderId: selectedFolder.id,
        destination: useMultiSelect ? (destinations.length === 1 ? destinations[0] : destinations) : selectedDestination,
        accountId,
        userId: user?.userId || user?.id
      };

      // Add advertiser ID if required
      if (requiresAdvertiserId && advertiserId.trim()) {
        campaignData.advertiserId = advertiserId.trim();
      }

      // Add platform account ID if required (for GroundTruth)
      if (requiresAccountId && accountIdField.trim()) {
        campaignData.platformAccountId = accountIdField.trim(); // Use platformAccountId instead of accountId
      }

      // Add profile ID if required (for Reddit)
      if (requiresProfileId && profileId.trim()) {
        campaignData.profileId = profileId.trim();
      }

      // Add page ID if required (for Facebook OAuth)
      if (isFacebookOAuthChannel && pageId.trim()) {
        campaignData.pageId = pageId.trim();
      }

      // Add ad account ID if required (for LinkedIn/Facebook non-OAuth)
      if (requiresAdAccountId && adAccountId.trim()) {
        campaignData.adAccountId = adAccountId.trim();
      }

      // Add customer ID if required (for Google Ads)
      if (requiresCustomerId && customerId.trim()) {
        campaignData.customerId = customerId.trim();
      }

      // Add MadHive product type if madhive is selected
      if (isMadhive) {
        campaignData.madhiveProductType = madhiveProductType;
      }

      // Add Inventory Mode for YouTube DV360 — drives launcher --inventory_mode CLI arg.
      if (currentDestinations.includes('youtube-dv360') && inventoryMode) {
        campaignData.inventoryMode = inventoryMode;
      }

      // Add IO type if MMGY account (default to 'mmgy' when IO type selector is hidden)
      if (isMMGYAccount) {
        // Setting may be: 'true' (legacy: all), 'false' (legacy: none),
        // or a comma-separated platform list like 'dv360,ttd'.
        // Use the user-selected ioType when at least one selected destination
        // has IO type enabled; otherwise default to 'mmgy'.
        campaignData.ioType = effectiveMmgyIoType || 'mmgy';
      }

      // Add channel ID if selected (for platforms with multiple configs)
      if (selectedChannelId && !isMultiChannelMode) {
        campaignData.channelId = selectedChannelId;
      }

      // Add uploaded file data
      if (uploadedFileData.length > 0) {
        campaignData.files = uploadedFileData;
      }

      // Add orchestration options - automatically enabled if files are present
      if (uploadedFileData.length > 0) {
        // Use recommendedType from analyze-files (same rules for single and multi-platform)
        // 2 files (PDF + JSON) = fast-track-shell
        // 3 files (PDF + JSON + Creative) = fast-track
        const workflowType = fileAnalysis?.recommendedType || 'fast-track-shell';

        campaignData.orchestration = {
          enabled: true,
          type: workflowType,
          priority: 'normal',
          autoStart: true,
          message: `Auto-triggered orchestration for campaign: ${campaignName.trim()}`,
          ...(planningMode && isPlanningModeAvailable && { planningMode: true })
        };

        // Log for debugging
        console.log(`📤 Frontend sending workflow type: ${workflowType} (from analyze-files, ${uploadedFileData.length} files)`);
      }

      const response = await api.post(getApiUrl('/api/v2/campaigns'), campaignData);
      const responseData = response.data;

      // Check if this is a multi-platform response
      const isMultiPlatform = responseData.multiPlatform === true;

      if (isMultiPlatform) {
        // Multi-platform campaign creation
        const campaigns = responseData.campaigns || [];
        const orchestrations = responseData.orchestrations || [];
        const summary = responseData.summary || {};

        console.log(`🌐 Multi-platform campaigns created:`, {
          total: summary.total,
          created: summary.created,
          failed: summary.failed,
          campaigns: campaigns.map(c => ({ id: c.id, name: c.name, destination: c.destination }))
        });

        // Build success message
        let successMessage = `Successfully created ${summary.created} campaign${summary.created > 1 ? 's' : ''}`;
        if (summary.failed > 0) {
          successMessage += ` (${summary.failed} failed)`;
        }
        if (orchestrations.length > 0) {
          successMessage += ` with orchestrations running`;
        }

        // Navigate to campaigns page filtered by folder to show all created campaigns
        navigate(`/campaigns?folderId=${selectedFolder.id}`, {
          state: {
            fromCampaignCreation: true,
            multiPlatform: true,
            campaigns: campaigns,
            orchestrations: orchestrations,
            summary: summary,
            message: successMessage
          }
        });
      } else {
        // Single campaign creation (existing behavior)
        const campaign = responseData;

        // Files are already associated with the campaign during creation
        // No need for additional file upload since campaignData.files contains the GCS file data

        // Handle orchestration response and navigation
        // Check if multi-channel template was detected and processed
        const isMultiChannelTemplate = campaign.multiChannel?.detectedFromTemplate;
        const totalCampaigns = campaign.multiChannel?.totalCampaigns || 1;

        let successMessage;
        if (isMultiChannelTemplate) {
          successMessage = `Multi-channel template detected! Created ${totalCampaigns} campaigns from your launch template.`;
        } else if (campaign.multiChannel && totalCampaigns > 1) {
          successMessage = `Created ${totalCampaigns} campaigns successfully!`;
        } else {
          successMessage = campaign.orchestration?.triggered
            ? `Campaign "${campaign.name}" created and orchestration started!`
            : `Campaign "${campaign.name}" created successfully!`;
        }

        // Always navigate to the primary campaign page to show orchestration progress
        navigate(`/campaigns/${campaign.id}`, {
          state: {
            fromCampaignCreation: true,
            orchestrationTriggered: campaign.orchestration?.triggered || false,
            orchestrationChatId: campaign.orchestration?.chatId,
            orchestrationType: campaign.orchestration?.type,
            orchestrationStatus: campaign.orchestration?.status,
            orchestrationError: campaign.orchestration?.error,
            multiChannel: campaign.multiChannel,
            message: successMessage
          }
        });
      }

    } catch (err) {
      console.error('Error creating campaign:', err);
      setError('Failed to create campaign: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * AI Campaign submit.
   *
   * Sends the brief instead of files. The backend writes it to GCS, hands the
   * orchestrator a campaign-processor preset pointing at it, and the launcher
   * builds the campaign from a MadHive template — so the response, the
   * navigation and the campaign detail page are identical to a file-based
   * create. Nothing is uploaded from the browser.
   */
  const handleAiSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedFolder || !campaignName.trim()) {
      setError('Please select a folder and enter a campaign name');
      return;
    }
    if (!aiChannel) {
      setError('Please select a channel');
      return;
    }
    if (!aiStartDate || !aiEndDate) {
      setError('Please enter a start date and an end date');
      return;
    }
    if (aiEndDate < aiStartDate) {
      setError('End date must be on or after the start date');
      return;
    }
    if (!(Number(aiBudget) > 0)) {
      setError('Please enter a budget greater than zero');
      return;
    }
    if (!aiBrandUrl.trim()) {
      setError('Please enter the brand URL');
      return;
    }
    if (requiresAdvertiserId && !advertiserId.trim()) {
      setError('Advertiser ID is required for this channel');
      return;
    }
    if (requiresAdAccountId && !adAccountId.trim()) {
      setError('Ad Account ID is required for this channel');
      return;
    }
    if (requiresCustomerId && !customerId.trim()) {
      setError('Customer ID is required for this channel');
      return;
    }
    if (isFacebookOAuthChannel && !pageId.trim()) {
      setError('Page ID is required for Facebook OAuth channels');
      return;
    }
    if (selectedDestination && hasMultipleChannels(selectedDestination) && !selectedChannelId) {
      setError('Please select a channel configuration for this platform');
      return;
    }
    if (!canSendMessage(user)) {
      setError('You do not have permission to create campaigns');
      return;
    }

    try {
      setIsLoading(true);

      const campaignData = {
        name: campaignName.trim(),
        folderId: selectedFolder.id,
        accountId,
        userId: user?.userId || user?.id,
        // The backend derives `destination` from aiCampaign.channel; sending it
        // too keeps the request readable in logs.
        destination: AI_CAMPAIGN_CHANNELS.find(c => c.id === aiChannel)?.destination,
        aiCampaign: {
          startDate: aiStartDate,
          endDate: aiEndDate,
          budget: aiBudget,
          channel: aiChannel,
          brandUrl: aiBrandUrl.trim(),
          geo: aiGeo.trim(),
          goal: aiGoal,
          additionalContext: aiContext.trim(),
        },
      };

      if (requiresAdvertiserId) campaignData.advertiserId = advertiserId.trim();
      if (requiresAdAccountId && adAccountId.trim()) {
        campaignData.adAccountId = adAccountId.trim();
      }
      if (requiresCustomerId && customerId.trim()) {
        campaignData.customerId = customerId.trim();
      }
      if (isFacebookOAuthChannel && pageId.trim()) {
        campaignData.pageId = pageId.trim();
      }
      if (selectedChannelId) {
        campaignData.channelId = selectedChannelId;
      }

      const response = await api.post(getApiUrl('/api/v2/campaigns'), campaignData);
      const campaign = response.data;

      navigate(`/campaigns/${campaign.id}`, {
        state: {
          fromCampaignCreation: true,
          orchestrationTriggered: campaign.orchestration?.triggered || false,
          orchestrationChatId: campaign.orchestration?.chatId,
          orchestrationType: campaign.orchestration?.type,
          orchestrationStatus: campaign.orchestration?.status,
          orchestrationError: campaign.orchestration?.error,
          message: `AI campaign "${campaign.name}" created — building the launch plan from your brief.`,
        }
      });
    } catch (err) {
      console.error('Error creating AI campaign:', err);
      setError('Failed to create AI campaign: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) { onCancel(); return; }
    navigate('/');
  };

  // Destination selection handlers
  const handleSingleDestinationSelect = (destinationId) => {
    setSelectedDestination(destinationId);
    setError(null); // Clear any previous errors
  };

  const handleMultiDestinationToggle = (destinationId) => {
    setSelectedDestinations(prev => {
      const isSelected = prev.includes(destinationId);
      const newSelection = isSelected
        ? prev.filter(id => id !== destinationId)
        : [...prev, destinationId];

      setError(null); // Clear any previous errors
      return newSelection;
    });
  };

  // Handle import campaign submission
  const handleImport = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields for import
    if (!selectedFolder) {
      setError('Please select a folder');
      return;
    }

    if (!selectedDestination) {
      setError('Please select a platform');
      return;
    }

    if (!platformCampaignId.trim()) {
      setError('Please enter the Campaign ID to import');
      return;
    }

    // Platform-specific validations
    const platform = selectedDestination.toLowerCase();

    // For Facebook: always require Ad Account ID
    if (platform === 'facebook') {
      if (!adAccountId.trim()) {
        setError('Ad Account ID is required for Facebook campaigns');
        return;
      }
    }

    // For LinkedIn: require Ad Account ID (bare numeric, no act_ prefix)
    if (platform === 'linkedin') {
      if (!adAccountId.trim()) {
        setError('Ad Account ID is required for LinkedIn campaigns');
        return;
      }
    }

    if ((platform === 'google-ads-search' || platform === 'google-ads-pmax' || platform === 'google-ads-demandgen') && !customerId.trim()) {
      setError('Customer ID is required for Google Ads campaigns');
      return;
    }

    if ((platform === 'dv360' || platform === 'ttd') && !advertiserId.trim()) {
      setError('Advertiser ID is required for ' + (platform === 'dv360' ? 'DV360' : 'TTD') + ' campaigns');
      return;
    }

    if (platform === 'ttd' && advertiserId.includes(',')) {
      setError('TTD Advertiser ID must be a single ID. Only one advertiser ID is supported.');
      return;
    }

    // Validate channel config selection when multiple configs exist
    if (hasMultipleChannels(selectedDestination) && !selectedChannelId) {
      setError('Please select a channel configuration for this platform');
      return;
    }

    try {
      setIsLoading(true);

      const importData = {
        folderId: selectedFolder.id,
        platform: selectedDestination,
        platformCampaignId: platformCampaignId.trim()
      };

      // Add platform-specific IDs
      if (platform === 'facebook') {
        // Always include Ad Account ID with act_ prefix
        let fbAdAccountId = adAccountId.trim();
        if (!fbAdAccountId.startsWith('act_')) {
          fbAdAccountId = `act_${fbAdAccountId}`;
        }
        importData.adAccountId = fbAdAccountId;
      } else if (platform === 'linkedin') {
        // LinkedIn ad account id is a bare numeric — no act_ prefix.
        importData.adAccountId = adAccountId.trim();
      } else if (platform === 'dv360' || platform === 'youtube-dv360' || platform === 'ttd') {
        importData.advertiserId = advertiserId.trim();
      } else if (platform === 'google-ads-search' || platform === 'google-ads-pmax' || platform === 'google-ads-demandgen') {
        importData.customerId = customerId.trim();
      }
      // TODO: Add Reddit profile ID when implemented

      // Add channel ID if selected (for platforms with multiple configs)
      if (selectedChannelId) {
        importData.channelId = selectedChannelId;
      }

      console.log('📥 Importing campaign:', importData);

      const response = await api.post(getApiUrl('/api/v2/campaigns/import'), importData, {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });

      const result = response.data;
      console.log('✅ Import campaign response:', result);

      if (result.success && result.campaign) {
        // Navigate to campaign detail page
        navigate(`/campaigns/${result.campaign.id}`, {
          state: {
            fromCampaignImport: true,
            message: `Campaign import started for "${result.campaign.name}"`
          }
        });
      } else {
        setError(result.error || 'Failed to import campaign');
      }

    } catch (err) {
      console.error('Error importing campaign:', err);
      setError('Failed to import campaign: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Get platforms that support import - filtered by account settings
  const getImportEnabledPlatforms = () => {
    // Base platforms that technically support import
    const baseSupportedPlatforms = ['google-ads-search', 'google-ads-pmax', 'google-ads-demandgen', 'facebook', 'linkedin', 'reddit', 'dv360', 'youtube-dv360', 'ttd'];

    // Check account settings for import-campaign
    const importCampaignSetting = accountSettings?.['import-campaign'];
    if (!importCampaignSetting) {
      // If no setting, no import is enabled
      return [];
    }

    // Parse the comma-separated list into canonical destination ids. The
    // alias handling (g-ads-search / search / fb / ...) lives in
    // getEnabledPlatforms so every platform-list setting resolves the
    // same spellings — this used to be a per-platform if-chain here.
    const enabledPlatforms = getEnabledPlatforms(importCampaignSetting);

    // Filter base supported platforms by what's enabled in account settings
    return baseSupportedPlatforms.filter(platform => enabledPlatforms.includes(platform));
  };

  const importSupportedPlatforms = getImportEnabledPlatforms();

  // Helper function to get workflow display names
  const getWorkflowDisplayName = (workflowType) => {
    const displayNames = {
      'fast-track': 'Fast Track',
      'fast-track-shell': 'Fast Track Shell',
      'salesforce-orchestration': 'Salesforce',
      'gt-api': 'GroundTruth'
    };
    return displayNames[workflowType] || workflowType;
  };

  if (!accountId) {
    return (
      <div className={`campaign-creation-container${embedded ? ' campaign-creation-embedded' : ''}`}>
        <div className="campaign-creation-error">
          <p>Please select an account to create campaigns.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`campaign-creation-container${embedded ? ' campaign-creation-embedded' : ''}`}>
      <div className="campaign-creation-header">
        <h1>
          {mode === 'import' ? 'Import Campaign'
            : mode === 'ai' ? 'AI Campaign'
              : 'Create New Campaign'}
        </h1>
        {/* Mode Toggle. Import needs at least one import-capable platform; AI
            Campaign needs at least one of its two channels enabled. Either can
            be absent, so the toggle renders when there is more than one mode. */}
        {(importSupportedPlatforms.length > 0 || availableAiChannels.length > 0) && (
          <div className="creation-mode-toggle">
            <button
              type="button"
              className={`mode-btn ${mode === 'create' ? 'active' : ''}`}
              onClick={() => {
                setMode('create');
                setError(null);
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Create New</span>
            </button>
            {importSupportedPlatforms.length > 0 && (
              <button
                type="button"
                className={`mode-btn ${mode === 'import' ? 'active' : ''}`}
                onClick={() => {
                  setMode('import');
                  setError(null);
                  // Reset multi-channel mode when switching to import
                  setUseMultiChannelMode(false);
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Import Existing</span>
              </button>
            )}
            {availableAiChannels.length > 0 && (
              <button
                type="button"
                className={`mode-btn ${mode === 'ai' ? 'active' : ''}`}
                onClick={() => {
                  setMode('ai');
                  setError(null);
                  // AI campaigns are always single-channel, and planning mode
                  // has no meaning without an uploaded plan to review.
                  setUseMultiChannelMode(false);
                  setPlanningMode(false);
                }}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                <span>AI Campaign</span>
              </button>
            )}
          </div>
        )}
      </div>

      <form
        onSubmit={mode === 'import' ? handleImport : mode === 'ai' ? handleAiSubmit : handleSubmit}
        className="campaign-creation-form"
      >
        {error && (
          <div className="campaign-creation-error">
            <p>{error}</p>
            <button type="button" onClick={() => setError(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Folder Selection */}
        <div className="form-group">
          <label id="folder-select-label">Select Folder *</label>
          <div className="folder-select-container">
            <button
              type="button"
              className={`folder-select-button ${showFolderDropdown ? 'active' : ''}`}
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              aria-labelledby="folder-select-label"
              aria-expanded={showFolderDropdown}
              aria-haspopup="listbox"
            >
              <FontAwesomeIcon icon={faFolder} />
              <span>
                {selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'Choose folder...'}
              </span>
              <FontAwesomeIcon 
                icon={showFolderDropdown ? faChevronUp : faChevronDown} 
                className="dropdown-arrow"
              />
            </button>

            {showFolderDropdown && (
              <div className="folder-dropdown">
                {/* Search input */}
                <div className="folder-search-container">
                  <FontAwesomeIcon icon={faSearch} className="folder-search-icon" />
                  <input
                    type="text"
                    className="folder-search-input"
                    placeholder="Search folders..."
                    value={folderSearchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFolderSearchTerm(value);
                      handleFolderSearch(value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Folder list with scroll */}
                <div
                  className="folder-list-container"
                  ref={folderDropdownRef}
                  onScroll={handleFolderDropdownScroll}
                >
                  {folders.length === 0 && !isLoading ? (
                    <div className="folder-empty-state">
                      <p>No folders found</p>
                      {folderSearchTerm && (
                        <p className="folder-empty-hint">Try a different search term</p>
                      )}
                    </div>
                  ) : (
                    folders.map(folder => (
                      <button
                        key={folder.id}
                        type="button"
                        className={`folder-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedFolder(folder);
                          setShowFolderDropdown(false);
                          setFolderSearchTerm('');
                        }}
                      >
                        <FontAwesomeIcon icon={faFolder} />
                        <span>{getFolderDisplayLabel(folder)}</span>
                        {selectedFolder?.id === folder.id && (
                          <FontAwesomeIcon icon={faCheck} className="check-icon" />
                        )}
                      </button>
                    ))
                  )}

                  {/* Loading indicator for infinite scroll */}
                  {isLoadingMoreFolders && (
                    <div className="folder-loading-more">
                      <p>Loading more folders...</p>
                    </div>
                  )}
                </div>

                <div className="folder-dropdown-divider" />

                {!showNewFolderForm ? (
                  <button
                    type="button"
                    className="folder-option create-new"
                    onClick={() => setShowNewFolderForm(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create new folder</span>
                  </button>
                ) : (
                  <div className="new-folder-form">
                    <input
                      type="text"
                      placeholder="Enter folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateFolder();
                        }
                      }}
                      autoFocus
                    />
                    <div className="new-folder-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleCreateFolder}
                        disabled={!newFolderName.trim() || isLoading}
                      >
                        Create
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setShowNewFolderForm(false);
                          setNewFolderName('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Campaign Name - shown for both create and AI modes (import names the
            campaign after the platform campaign it pulls in) */}
        {mode !== 'import' && (
          <div className="form-group">
            <label htmlFor="campaign-name">Campaign Name *</label>
            <input
              type="text"
              id="campaign-name"
              className="form-control"
              placeholder="Enter campaign name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              required
            />
          </div>
        )}

        {/* AI Campaign brief — the whole input. No files: the launcher reads the
            brand's own site to work out keywords, interests and ad copy. */}
        {mode === 'ai' && (
          <div className="ai-campaign-brief">
            {aiChannel === 'universal-ads' && <p className="form-help-text">Create a CPM reach plan with campaign and ad sets. Review targeting and budgets before publishing. Campaigns launch paused; add video creatives later. Default CPM bid: $25.</p>}
            <div className="form-group">
              <label id="ai-channel-label">Channel *</label>
              <div className="destination-grid" role="radiogroup" aria-labelledby="ai-channel-label">
                {availableAiChannels.map(channel => (
                  <button
                    key={channel.id}
                    type="button"
                    className={`destination-option ${aiChannel === channel.id ? 'selected' : ''}`}
                    onClick={() => handleAiChannelSelect(channel.id)}
                    role="radio"
                    aria-checked={aiChannel === channel.id}
                  >
                    <span className="material-symbols-outlined destination-icon">
                      {channel.icon}
                    </span>
                    <span className="destination-name">{channel.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label id="ai-goal-label">Goal *</label>
              <div className="ai-goal-options" role="radiogroup" aria-labelledby="ai-goal-label">
                {AI_CAMPAIGN_GOALS.filter(goal => aiChannel !== 'universal-ads' || goal.id === 'reach').map(goal => (
                  <button
                    key={goal.id}
                    type="button"
                    className={`ai-goal-option ${aiGoal === goal.id ? 'selected' : ''}`}
                    onClick={() => setAiGoal(goal.id)}
                    role="radio"
                    aria-checked={aiGoal === goal.id}
                  >
                    <span className="ai-goal-name">{goal.name}</span>
                    <span className="ai-goal-help">{goal.help}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="ai-field-row">
              <div className="form-group">
                <label htmlFor="ai-start-date">Start Date *</label>
                <input
                  type="date"
                  id="ai-start-date"
                  className="form-control"
                  value={aiStartDate}
                  onChange={(e) => setAiStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="ai-end-date">End Date *</label>
                <input
                  type="date"
                  id="ai-end-date"
                  className="form-control"
                  value={aiEndDate}
                  min={aiStartDate || undefined}
                  onChange={(e) => setAiEndDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="ai-budget">Budget *</label>
                <input
                  type="number"
                  id="ai-budget"
                  className="form-control"
                  placeholder="e.g. 15000"
                  min="1"
                  step="any"
                  value={aiBudget}
                  onChange={(e) => setAiBudget(e.target.value)}
                  required
                />
                <p className="form-help-text">Total budget for the whole flight.</p>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ai-brand-url">Brand URL *</label>
              <input
                type="text"
                id="ai-brand-url"
                className="form-control"
                placeholder="https://yourbrand.com"
                value={aiBrandUrl}
                onChange={(e) => setAiBrandUrl(e.target.value)}
                required
              />
              <p className="form-help-text">
                We read this site to work out what the brand sells, then build the
                keywords, interests and ad copy from it. It is also the landing page.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="ai-geo">Geography</label>
              <input
                type="text"
                id="ai-geo"
                className="form-control"
                placeholder={aiChannel === 'universal-ads' ? 'United States, or 90026, 90027' : 'e.g. California, or Chicago DMA, or 90210, 90211'}
                value={aiGeo}
                onChange={(e) => setAiGeo(e.target.value)}
              />
              <p className="form-help-text">
                {aiChannel === 'universal-ads' ? 'Use US ZIP codes or United States. Leave blank for US nationwide.' : 'Optional. States, cities, DMAs or ZIP codes. Leave blank to run nationally.'}
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="ai-context">Additional Context</label>
              <textarea
                id="ai-context"
                className="form-control"
                rows={4}
                placeholder="Anything the campaign should know — the product to push, the audience to reach, competitors to target, things to avoid."
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
              />
              <p className="form-help-text">Optional.</p>
            </div>
          </div>
        )}

        {/* Planning Mode Toggle - Show when any enabled platform has the feature flag */}
        {mode === 'create' && isPlanningModeAvailable && (
          <div className="form-group planning-mode-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={planningMode}
                onChange={(e) => setPlanningMode(e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">
                Planning Mode
                <span className="toggle-description">
                  Review and edit campaign structure before launching to platform. Select a platform below.
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Destination Selection. AI mode has its own channel picker above and
            sets `selectedDestination` from it, so the grid is redundant there. */}
        {mode !== 'ai' && (
        <div className="form-group">
          <label id="destination-select-label">
            {mode === 'import' ? 'Select Platform *' : (
              isMultiChannelMode ? 'Select Destinations *' : 'Select Destination *'
            )}
          </label>
          <div className="destination-account-context">
            <p className="form-help-text">Showing destinations enabled for <strong>{accountName || 'the selected account'}</strong>.</p>
            <button type="button" className="text-button" disabled={channelsLoading || channelConfigsLoading} onClick={() => { refetchEnabledChannels(); refetchChannelConfigs(); }}>Refresh channels</button>
          </div>
          {channelsError && <p className="error-message" role="alert">Could not load this account's channels. Refresh channels to try again.</p>}
          {/* Multi-channel mode toggle - only show in create mode, and not in
              planning mode (planning is always single-select so it can capture
              a channel config). */}
          {mode === 'create' && isMultiChannelAvailable && !planningMode && (
            <div className="channel-mode-selector">
              <div className="mode-toggle-buttons">
                <button
                  type="button"
                  className={`mode-toggle-btn ${!useMultiChannelMode ? 'active' : ''}`}
                  onClick={() => setUseMultiChannelMode(false)}
                >
                  Single Channel
                </button>
                <button
                  type="button"
                  className={`mode-toggle-btn ${useMultiChannelMode ? 'active' : ''}`}
                  onClick={() => setUseMultiChannelMode(true)}
                >
                  Multi-Channel
                </button>
              </div>
              <p className="form-help-text mode-help">
                {useMultiChannelMode
                  ? 'Select multiple destinations for this campaign.'
                  : 'Choose one destination for this campaign.'
                }
              </p>
            </div>
          )}
          {/* Import mode help text */}
          {mode === 'import' && (
            <p className="form-help-text">
              Select the platform where your campaign exists.
            </p>
          )}
          <div
            className="destination-grid"
            role={isMultiChannelMode && mode === 'create' ? "group" : "radiogroup"}
            aria-labelledby="destination-select-label"
          >
            {channelsLoading || settingsLoading ? (
              <div className="loading-destinations">
                <p>Loading available destinations...</p>
              </div>
            ) : (mode === 'import' ?
              // Import mode: show only import-supported platforms
              availableDestinations
                .filter(dest => importSupportedPlatforms.includes(dest.id))
                .length === 0 ? (
                <div className="no-destinations">
                  <p>No import-supported platforms are enabled for this account.</p>
                  <p>Contact your administrator to enable Google Ads Search or Facebook.</p>
                </div>
              ) : (
                availableDestinations
                  .filter(dest => importSupportedPlatforms.includes(dest.id))
                  .map(destination => {
                    const isSelected = selectedDestination === destination.id;
                    return (
                      <button
                        key={destination.id}
                        type="button"
                        className={`destination-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSingleDestinationSelect(destination.id)}
                        role="radio"
                        aria-checked={isSelected}
                      >
                        <span className="material-symbols-outlined destination-icon">
                          {destination.icon}
                        </span>
                        <span className="destination-name">{destination.name}</span>
                        {isSelected && (
                          <FontAwesomeIcon icon={faCheck} className="check-icon" />
                        )}
                      </button>
                    );
                  })
              )
            : (
              // Create mode: show filtered destinations as before
              filteredDestinations.length === 0 ? (
                <div className="no-destinations">
                  {isMultiChannelMode ? (
                    <>
                      <p>No destinations available for multi-channel mode.</p>
                      <p>The multi-channels setting ({allowedChannels.join(', ')}) contains channels that are not enabled for this account.</p>
                      <p>Please contact your administrator to enable these channels or update the multi-channels setting.</p>
                    </>
                  ) : (
                    <>
                      <p>No destinations available for this account.</p>
                      <p>Please contact your administrator to configure channels.</p>
                    </>
                  )}
                </div>
              ) : (
                filteredDestinations.map(destination => {
                  const useMultiSelect = isMultiChannelMode;
                  const isSelected = useMultiSelect
                    ? selectedDestinations.includes(destination.id)
                    : selectedDestination === destination.id;

                  return (
                    <button
                      key={destination.id}
                      type="button"
                      className={`destination-option ${isSelected ? 'selected' : ''} ${useMultiSelect ? 'multi-select' : ''}`}
                      onClick={() => useMultiSelect
                        ? handleMultiDestinationToggle(destination.id)
                        : handleSingleDestinationSelect(destination.id)
                      }
                      role={useMultiSelect ? "checkbox" : "radio"}
                      aria-checked={isSelected}
                    >
                      <span className="material-symbols-outlined destination-icon">
                        {destination.icon}
                      </span>
                      <span className="destination-name">{destination.name}</span>
                      {isSelected && (
                        <FontAwesomeIcon icon={faCheck} className="check-icon" />
                      )}
                    </button>
                  );
                })
              )
            ))}
          </div>
        </div>
        )}

        {/* Import Mode: Campaign ID field */}
        {mode === 'import' && selectedDestination && (
          <div className="form-group">
            <label htmlFor="platform-campaign-id">Campaign ID *</label>
            <input
              type="text"
              id="platform-campaign-id"
              className="form-control"
              placeholder={
                (selectedDestination === 'google-ads-search' || selectedDestination === 'google-ads-pmax' || selectedDestination === 'google-ads-demandgen') ? 'Enter Google Ads Campaign ID (e.g., 23330174154)' :
                selectedDestination === 'facebook' ? 'Enter Facebook Campaign ID (e.g., 120239629756170437)' :
                selectedDestination === 'ttd' ? 'Enter Campaign ID(s), comma-separated (e.g., cmp1,cmp2)' :
                'Enter Campaign ID'
              }
              value={platformCampaignId}
              onChange={(e) => setPlatformCampaignId(e.target.value)}
              required
            />
            <p className="form-help-text">
              {(selectedDestination === 'google-ads-search' || selectedDestination === 'google-ads-pmax' || selectedDestination === 'google-ads-demandgen')
                ? 'The Campaign ID from your Google Ads account'
                : selectedDestination === 'facebook'
                ? 'The Campaign ID from Facebook Ads Manager'
                : selectedDestination === 'ttd'
                ? 'One or more TTD Campaign IDs, comma-separated'
                : 'The Campaign ID from the platform'
              }
            </p>
          </div>
        )}

        {/* Import Mode: Channel Config Selection - Show when platform has multiple configs */}
        {mode === 'import' && selectedDestination && hasMultipleChannels(selectedDestination) && (
          <div className="form-group">
            <label htmlFor="import-channel-config">Channel Configuration *</label>
            {channelConfigsLoading ? (
              <p className="form-help-text">Loading channel configurations...</p>
            ) : (
              <>
                <select
                  id="import-channel-config"
                  className="form-control"
                  value={selectedChannelId || ''}
                  onChange={(e) => setSelectedChannelId(e.target.value)}
                  required
                >
                  <option value="">Select a channel configuration</option>
                  {getChannelsForPlatform(selectedDestination).map(channel => {
                    // For non-Facebook platforms, show ad_account_id if available
                    // For Facebook, just show the channel name (ad_account_id is entered separately)
                    const showAdAccountId = selectedDestination !== 'facebook' && channel.field_values?.ad_account_id;
                    const displayText = showAdAccountId
                      ? `${channel.name} (${channel.field_values.ad_account_id})`
                      : channel.name;
                    return (
                      <option key={channel.id} value={channel.id}>
                        {displayText}
                      </option>
                    );
                  })}
                </select>
                <p className="form-help-text">
                  Multiple {availableDestinations.find(d => d.id === selectedDestination)?.name || selectedDestination} configurations are available. Select which one to use for importing this campaign.
                </p>
              </>
            )}
          </div>
        )}

        {/* Import Mode: Ad Account ID for Facebook / LinkedIn */}
        {mode === 'import' && (selectedDestination === 'facebook' || selectedDestination === 'linkedin') && (
          <div className="form-group">
            <label htmlFor="import-ad-account-id">Ad Account ID *</label>
            <input
              type="text"
              id="import-ad-account-id"
              className="form-control"
              placeholder={selectedDestination === 'linkedin'
                ? 'Enter LinkedIn Ad Account ID (e.g., 513547346)'
                : 'Enter Facebook Ad Account ID (e.g., 821587643709084)'}
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              required
            />
            <p className="form-help-text">
              {selectedDestination === 'linkedin'
                ? 'Your LinkedIn Ad Account ID (bare numeric, e.g., 513547346)'
                : 'Your Facebook Ad Account ID (e.g., 123456789)'}
            </p>
          </div>
        )}

        {/* Import Mode: Customer ID for Google Ads */}
        {mode === 'import' && (selectedDestination === 'google-ads-search' || selectedDestination === 'google-ads-pmax' || selectedDestination === 'google-ads-demandgen') && (
          <div className="form-group">
            <label htmlFor="import-customer-id">Customer ID *</label>
            <input
              type="text"
              id="import-customer-id"
              className="form-control"
              placeholder="Enter Google Ads Customer ID (e.g., 1234567890)"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              required
            />
            <p className="form-help-text">
              Your Google Ads Customer ID (also known as CID). You can find it in the top right corner of your Google Ads account.
            </p>
          </div>
        )}

        {/* Import Mode: Advertiser ID for DV360, YouTube DV360, and TTD */}
        {mode === 'import' && (selectedDestination === 'dv360' || selectedDestination === 'youtube-dv360' || selectedDestination === 'ttd') && (
          <div className="form-group">
            <label htmlFor="import-advertiser-id">Advertiser ID *</label>
            <input
              type="text"
              id="import-advertiser-id"
              className="form-control"
              placeholder={
                selectedDestination === 'dv360' ? 'Enter DV360 Advertiser ID (e.g., 12345678)' :
                selectedDestination === 'youtube-dv360' ? 'Enter YouTube DV360 Advertiser ID (e.g., 1435831)' :
                'Enter TTD Advertiser ID (e.g., abcd1234)'
              }
              value={advertiserId}
              onChange={(e) => setAdvertiserId(e.target.value)}
              required
            />
            <p className="form-help-text">
              {selectedDestination === 'dv360'
                ? 'Your DV360 Advertiser ID (e.g., 12345678)'
                : selectedDestination === 'youtube-dv360'
                ? 'Your YouTube DV360 Advertiser ID (e.g., 1435831)'
                : 'Your Trade Desk Advertiser ID (e.g., abcd1234). Only one advertiser ID is supported.'}
            </p>
          </div>
        )}

        {/* Advertiser ID - Show for DV360, TTD, MadHive, etc. Required in both planning and
            non-planning mode so the ID is captured upfront and persisted at creation. */}
        {(mode === 'create' || mode === 'ai') && requiresAdvertiserId && (
          <div className="form-group">
            <label htmlFor="advertiser-id">Advertiser ID *</label>
            <input
              type="text"
              id="advertiser-id"
              className="form-control"
              placeholder={advertiserIdCopy.placeholder}
              value={advertiserId}
              onChange={(e) => setAdvertiserId(e.target.value)}
              required
            />
            <p className="form-help-text">{advertiserIdCopy.help}</p>
          </div>
        )}

        {/* YouTube DV360 Inventory Mode — drives launcher --inventory_mode CLI arg.
            Default Standard for MMGY accounts, Expanded otherwise (matches the
            campaign-services handler fallback). */}
        {mode === 'create' && currentDestinations.includes('youtube-dv360') && (
          <div className="form-group">
            <label htmlFor="inventory-mode">Inventory Mode *</label>
            <select
              id="inventory-mode"
              className="form-control"
              value={inventoryMode}
              onChange={(e) => setInventoryMode(e.target.value)}
            >
              <option value="Standard">Standard</option>
              <option value="Expanded">Expanded</option>
            </select>
            <p className="form-help-text">
              {isMMGYAccount
                ? 'MMGY accounts default to Standard inventory.'
                : 'Default Expanded — pick Standard if your team is transitioning inventory modes.'}
            </p>
          </div>
        )}

        {/* MadHive Product Type - Show when destination is madhive (create mode, including planning mode) */}
        {mode === 'create' && isMadhive && (
          <div className="form-group">
            <label htmlFor="madhive-product-type">Madhive Product Type *</label>
            <select
              id="madhive-product-type"
              className="form-control"
              value={madhiveProductType}
              onChange={(e) => setMadhiveProductType(e.target.value)}
            >
              <option value="ctv">CTV</option>
              <option value="display">Display</option>
            </select>
            <p className="form-help-text">Select the MadHive product type for this campaign</p>
          </div>
        )}

        {/* MMGY IO Type - Show only when account is MMGY (create mode only)
            and the setting is enabled for at least one selected destination.
            Setting may be 'true' (all), 'false'/empty (none), or 'dv360,ttd' (per-platform). */}
        {mode === 'create' && isMMGYAccount &&
          isEnabledForAnyOfPlatforms(accountSettings?.['enable-mmgy-io-type'], currentDestinations) && (
          <div className="form-group">
            <label htmlFor="io-type">IO Type *</label>
            <select
              id="io-type"
              className="form-control"
              value={ioType}
              onChange={(e) => setIoType(e.target.value)}
            >
              <option value="mmgy">MMGY</option>
              <option value="traveldesk">TravelDesk</option>
              {mmgyPlanOnlyAvailable && (
                <option value={MMGY_PLAN_ONLY_IO_TYPE}>Plan only (Excel)</option>
              )}
            </select>
            <p className="form-help-text">
              {MMGY_IO_TYPE_HELP[ioType] || MMGY_IO_TYPE_HELP.mmgy}
            </p>
          </div>
        )}

        {/* Customer ID - Show only for Google Ads (create + AI modes, hidden in planning mode) */}
        {mode !== 'import' && !planningMode && requiresCustomerId && (
          <div className="form-group">
            <label htmlFor="customer-id">Customer ID *</label>
            <input
              type="text"
              id="customer-id"
              className="form-control"
              placeholder="Enter Google Ads Customer ID (e.g., 1234567890)"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              required
            />
            <p className="form-help-text">
              Your Google Ads Customer ID (also known as CID). You can find it in the top right corner of your Google Ads account.
            </p>
          </div>
        )}

        {/* Account ID - Show only for GroundTruth (create mode only, hidden in planning mode) */}
        {mode === 'create' && !planningMode && requiresAccountId && (
          <div className="form-group">
            <label htmlFor="account-id">Account ID *</label>
            <input
              type="text"
              id="account-id"
              className="form-control"
              placeholder="Enter GroundTruth Account ID"
              value={accountIdField}
              onChange={(e) => setAccountIdField(e.target.value)}
              required
            />
            <p className="form-help-text">
              Your GroundTruth Account ID (e.g., gt_account_123)
            </p>
          </div>
        )}

        {/* Profile ID - Show only for Reddit (create mode only, hidden in planning mode) */}
        {mode === 'create' && !planningMode && requiresProfileId && (
          <div className="form-group">
            <label htmlFor="profile-id">Profile ID *</label>
            <input
              type="text"
              id="profile-id"
              className="form-control"
              placeholder="Enter Reddit Profile ID"
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              required
            />
            <p className="form-help-text">
              Your Reddit Profile ID (e.g., t2_1kehyjrre5)
            </p>
          </div>
        )}

        {/* Channel Config Selection - Show when platform has multiple configs (single channel mode, including planning mode, so channel_id is captured up front) */}
        {mode === 'create' && selectedDestination && !isMultiChannelMode && hasMultipleChannels(selectedDestination) && (
          <div className="form-group">
            <label htmlFor="channel-config">Channel Configuration *</label>
            {channelConfigsLoading ? (
              <p className="form-help-text">Loading channel configurations...</p>
            ) : (
              <>
                <select
                  id="channel-config"
                  className="form-control"
                  value={selectedChannelId || ''}
                  onChange={(e) => setSelectedChannelId(e.target.value)}
                  required
                >
                  <option value="">Select a channel configuration</option>
                  {getChannelsForPlatform(selectedDestination).map(channel => {
                    // For non-Facebook platforms, show ad_account_id if available
                    // For Facebook, just show the channel name (ad_account_id is entered separately)
                    const showAdAccountId = selectedDestination !== 'facebook' && channel.field_values?.ad_account_id;
                    const displayText = showAdAccountId
                      ? `${channel.name} (${channel.field_values.ad_account_id})`
                      : channel.name;
                    return (
                      <option key={channel.id} value={channel.id}>
                        {displayText}
                      </option>
                    );
                  })}
                </select>
                <p className="form-help-text">
                  Multiple {availableDestinations.find(d => d.id === selectedDestination)?.name || selectedDestination} configurations are available. Select which one to use for this campaign.
                </p>
              </>
            )}
          </div>
        )}

        {/* Ad Account ID - Show only for LinkedIn/Facebook (create + AI modes, hidden in planning mode) */}
        {mode !== 'import' && !planningMode && requiresAdAccountId && (
          <div className="form-group">
            <label htmlFor="ad-account-id">Ad Account ID *</label>
            <input
              type="text"
              id="ad-account-id"
              className="form-control"
              placeholder={getAdAccountIdPlaceholder()}
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              required
            />
            <p className="form-help-text">
              {getAdAccountIdHelpText()}
            </p>
          </div>
        )}

        {/* Page ID - Show for Facebook OAuth channels (create + AI modes) */}
        {mode !== 'import' && isFacebookOAuthChannel && (
          <div className="form-group">
            <label htmlFor="fb-page-id">Page ID *</label>
            <input
              type="text"
              id="fb-page-id"
              className="form-control"
              placeholder="Enter Facebook Page ID"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              required
            />
            <p className="form-help-text">
              Your Facebook Page ID for OAuth authentication
            </p>
          </div>
        )}

        {/* File Upload - Only show in create mode */}
        {mode === 'create' && (
        <div className="form-group">
          <label htmlFor="file-upload">Upload Files *</label>
          <div
            className={`file-upload-area ${
              isUploadingFiles
                ? 'uploading'
                : uploadedFiles.length === 0
                  ? (hasInteracted ? 'required-empty' : '')
                  : 'has-files'
            } ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileUpload}
              className="file-input"
              required={uploadedFiles.length === 0}
              aria-describedby="file-upload-help"
              disabled={isUploadingFiles}
            />
            <div className="file-upload-content">
              <FontAwesomeIcon icon={faUpload} className="upload-icon" />
              <p>Drag your files here to start uploading</p>
              <p id="file-upload-help" className="file-upload-required">
                {isUploadingFiles
                  ? 'Uploading files...'
                  : isValidating || isRevalidating
                    ? 'Validating files...'
                    : uploadedFiles.length === 0
                      ? (hasInteracted ? 'At least one file is required' : 'Please select files to upload')
                      : `${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''} uploaded`
                }
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setHasInteracted(true);
                  document.getElementById('file-upload').click();
                }}
                disabled={isUploadingFiles}
              >
                {isUploadingFiles ? 'Uploading...' : 'Browse files'}
              </button>
            </div>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="uploaded-files-list">
              <h4>Uploaded Files ({uploadedFiles.length})</h4>
              {uploadedFiles.map((file, index) => {
                const fileData = uploadedFileData[index];
                const hasErrors = fileData?.validation && !fileData.validation.valid && fileData.validation.errors?.length > 0;
                const hasWarnings = fileData?.validation && fileData.validation.warnings?.length > 0;

                return (
                  <div key={index} style={{ marginBottom: '15px' }}>
                    <div className="uploaded-file-item">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => removeFile(index)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    {hasErrors && (
                      <div style={{
                        marginTop: '8px',
                        marginLeft: '10px',
                        fontSize: '13px',
                        color: '#d32f2f',
                        backgroundColor: '#ffebee',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        borderLeft: '3px solid #d32f2f'
                      }}>
                        {fileData.validation.errors.map((error, i) => (
                          <div key={i} style={{ marginBottom: i < fileData.validation.errors.length - 1 ? '6px' : '0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ flexShrink: 0 }}>❌</span>
                            <span>{error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {hasWarnings && !hasErrors && (
                      <div style={{
                        marginTop: '8px',
                        marginLeft: '10px',
                        fontSize: '13px',
                        color: '#f57c00',
                        backgroundColor: '#fff3e0',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        borderLeft: '3px solid #f57c00'
                      }}>
                        {fileData.validation.warnings.map((warning, i) => (
                          <div key={i} style={{ marginBottom: i < fileData.validation.warnings.length - 1 ? '6px' : '0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ flexShrink: 0 }}>⚠️</span>
                            <span>{warning}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Multi-platform detection notice */}
          {fileAnalysis?.multiPlatform?.isMultiPlatform && (
            <div className="form-group" style={{ marginTop: '15px' }}>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '20px' }}>🌐</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#1976d2' }}>
                    Multi-Platform Launch Template Detected
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#555' }}>
                    This template contains {fileAnalysis.multiPlatform.platformCount} platforms: {' '}
                    {fileAnalysis.multiPlatform.platforms.map(p => {
                      const dest = availableDestinations.find(d => d.id === p);
                      return dest?.name || p;
                    }).join(', ')}
                  </p>
                  {isMultiChannelMode && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#1976d2', fontWeight: '500' }}>
                      ✓ Multi-channel mode enabled - {selectedDestinations.length} platform{selectedDestinations.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Auto-orchestration info */}
        {/* {uploadedFiles.length > 0 && (
          <div className="form-group orchestration-info">
            <div className="orchestration-notice">
              <FontAwesomeIcon icon={faCheck} className="orchestration-icon" />
              <div>
                <p className="orchestration-title">
                  {isAnalyzingFiles ? 'Analyzing files...' : 'Auto-orchestration enabled'}
                </p>
                <p className="orchestration-description">
                  {isAnalyzingFiles ? (
                    'Determining the best workflow for your files'
                  ) : fileAnalysis ? (
                    `${fileAnalysis.recommendedType ?
                      `Detected: ${getWorkflowDisplayName(fileAnalysis.recommendedType)} workflow` :
                      'Files will be processed automatically'
                    } (${Math.round(fileAnalysis.confidence * 100)}% confidence)`
                  ) : (
                    'Your files will be automatically processed after campaign creation'
                  )}
                </p>
              </div>
            </div>
          </div>
        )} */}

        {/* Review Result Display - shown above form actions.
            Adara (DV360/TTD) uses "Launch Template" wording; GT keeps "CTS". */}
        {ctsReviewResult && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: ctsReviewResult.success ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${ctsReviewResult.success ? '#bbf7d0' : '#fecaca'}`,
          }}>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: ctsReviewResult.success ? '#166534' : '#991b1b' }}>
              {(() => {
                const label = isAdaraAccount ? 'Launch Template Review' : 'CTS Review';
                return ctsReviewResult.success ? `${label} Passed` : `${label} Found Issues`;
              })()}
              {ctsReviewResult.campaign_name && <span style={{ fontWeight: '400', marginLeft: '8px' }}>— {ctsReviewResult.campaign_name}</span>}
            </div>
            {ctsReviewResult.errors?.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: '500', color: '#991b1b', marginBottom: '4px' }}>Errors ({ctsReviewResult.errors.length}):</div>
                {ctsReviewResult.errors.map((err, i) => (
                  <div key={i} style={{ color: '#991b1b', fontSize: '13px', padding: '2px 0', paddingLeft: '12px' }}>• {err}</div>
                ))}
              </div>
            )}
            {ctsReviewResult.warnings?.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: '500', color: '#92400e', marginBottom: '4px' }}>Warnings ({ctsReviewResult.warnings.length}):</div>
                {ctsReviewResult.warnings.map((warn, i) => (
                  <div key={i} style={{ color: '#92400e', fontSize: '13px', padding: '2px 0', paddingLeft: '12px' }}>• {warn}</div>
                ))}
              </div>
            )}
            {ctsReviewResult.infos?.length > 0 && (
              <div>
                <div style={{ fontWeight: '500', color: '#1e40af', marginBottom: '4px' }}>Info ({ctsReviewResult.infos.length}):</div>
                {ctsReviewResult.infos.map((info, i) => (
                  <div key={i} style={{ color: '#1e40af', fontSize: '13px', padding: '2px 0', paddingLeft: '12px' }}>• {info}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          {mode === 'import' ? (
            // Import mode submit button
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                isLoading ||
                !selectedFolder ||
                !selectedDestination ||
                !platformCampaignId.trim() ||
                // Facebook / LinkedIn require ad account ID
                ((selectedDestination === 'facebook' || selectedDestination === 'linkedin') && !adAccountId.trim()) ||
                // Google Ads requires customer ID
                ((selectedDestination === 'google-ads-search' || selectedDestination === 'google-ads-pmax' || selectedDestination === 'google-ads-demandgen') && !customerId.trim()) ||
                // DV360 and TTD require advertiser ID
                ((selectedDestination === 'dv360' || selectedDestination === 'ttd') && !advertiserId.trim()) ||
                // Require channel selection when multiple configs exist
                (hasMultipleChannels(selectedDestination) && !selectedChannelId)
              }
            >
              {isLoading ? 'Importing campaign...' : 'Import Campaign'}
            </button>
          ) : mode === 'ai' ? (
            // AI mode submit button — no upload step, so the only gate is a
            // complete brief.
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || !aiBriefComplete}
            >
              {isLoading ? 'Building campaign from your brief...' : 'Create AI Campaign'}
            </button>
          ) : (
            // Create mode buttons
            <>
              {/* Review CTS / Launch Template button:
                    - GroundTruth account: always
                    - Adara account: only when DV360 or TTD destination is selected
                    - Any other account: never */}
              {showReviewCtsButton && uploadedFileData.length > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleReviewCts}
                  disabled={isReviewingCts || isLoading || uploadedFileData.length === 0}
                >
                  {isAdaraAccount
                    ? (isReviewingCts ? 'Reviewing Template...' : 'Review Template')
                    : (isReviewingCts ? 'Reviewing CTS...' : 'Review CTS')}
                </button>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  isLoading ||
                  isUploadingFiles ||
                  isValidating ||
                  isRevalidating ||
                  // analyze-files rejected the upload for an account
                  // requirement (missing launch template / insertion order)
                  !!accountRequirementError ||
                  uploadedFileData.some(file =>
                    file.validation && !file.validation.valid && file.validation.errors?.length > 0
                  ) ||
                  !selectedFolder ||
                  !campaignName.trim() ||
                  getCurrentDestinations().length === 0 ||
                  uploadedFiles.length === 0 ||
                  (!planningMode && requiresAdvertiserId && !advertiserId.trim()) ||
                  (!planningMode && requiresAccountId && !accountIdField.trim()) ||
                  (!planningMode && requiresProfileId && !profileId.trim()) ||
                  (!planningMode && requiresAdAccountId && !adAccountId.trim()) ||
                  (!isMultiChannelMode && selectedDestination && hasMultipleChannels(selectedDestination) && !selectedChannelId)
                }
              >
                {isLoading ? (
                  uploadedFileData.length > 0 ? 'Creating campaign and starting orchestration...' : 'Creating campaign...'
                ) : isUploadingFiles ? 'Uploading files...' : 'Create Campaign'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default CampaignCreation;
